import {
  ArchiveIcon,
  BookmarkIcon,
  GearIcon,
  HamburgerMenuIcon,
  MagnifyingGlassIcon,
  TrashIcon,
} from "@radix-ui/react-icons";
import * as React from "react";
import { Routes, routes } from "../../routing/router.ts";
import { Signal } from "@repo/shared/utils/signals/Signal.ts";
import { CommonDialogsContextValue } from "../CommonDialogsProvider.tsx";

export type NavItemData = {
  icon: React.ReactNode;
  label: string;
  /** Where it goes, for a real link (so it can be opened in a new tab) */
  href?: string;
  onClick?: (options: { commonDialogs: CommonDialogsContextValue | null }) => void;
  getIsSelected: (options: { route: Routes }) => boolean;
  keyboardShortcutHint?: string;
  /** Shows how many items are in that list beside it */
  countKind?: "unread" | "archived" | "trashed";
};

const stashItem: NavItemData = {
  href: routes.stash().href,
  onClick: () => {
    routes.stash().push();
    scrollToTopSignal.dispatch();
  },
  getIsSelected: ({ route }) => route.name === "stash",
  icon: <BookmarkIcon />,
  label: "Stash",
  countKind: "unread",
};

const searchItem: NavItemData = {
  getIsSelected: () => false,
  icon: <MagnifyingGlassIcon />,
  label: "Search",
  onClick: ({ commonDialogs }) => commonDialogs?.openSearchDialog(),
  keyboardShortcutHint: "Ctrl P",
};

const archiveItem: NavItemData = {
  href: routes.archive().href,
  onClick: () => {
    routes.archive().push();
    scrollToTopSignal.dispatch();
  },
  getIsSelected: ({ route }) => route.name === "archive",
  icon: <ArchiveIcon />,
  label: "Archive",
  countKind: "archived",
};

const trashItem: NavItemData = {
  href: routes.trash().href,
  onClick: () => {
    routes.trash().push();
    scrollToTopSignal.dispatch();
  },
  getIsSelected: ({ route }) => route.name === "trash",
  icon: <TrashIcon />,
  label: "Trash",
  countKind: "trashed",
};

const settingsItem: NavItemData = {
  href: routes.settings().href,
  getIsSelected: ({ route }) => route.name === "settings",
  onClick: () => routes.settings().push(),
  icon: <GearIcon />,
  label: "Settings",
};

export const searchNavItem = searchItem;

/** The sidebar's lists, with Settings at the foot of it */
export const sidebarNavItems: NavItemData[] = [stashItem, archiveItem, trashItem];
export const sidebarFooterItems: NavItemData[] = [settingsItem];

export const bottomNavItems: NavItemData[] = [
  stashItem,
  searchItem,
  archiveItem,
  {
    getIsSelected: ({ route }) => route.name === "trash" || route.name === "settings",
    onClick: ({ commonDialogs }) => commonDialogs?.openMoreMenu(),
    icon: <HamburgerMenuIcon />,
    label: "More",
  },
];

export const moreMenuItems: NavItemData[] = [trashItem, settingsItem];

export const scrollToTopSignal = new Signal();

/**
 * Runs a nav item's own navigation for a plain click, and leaves cmd/ctrl/middle clicks on its link
 * to the browser, so they open in a new tab.
 */
export const onNavClick =
  (item: NavItemData, commonDialogs: CommonDialogsContextValue | null) =>
  (event: React.MouseEvent) => {
    if (item.href && (event.metaKey || event.ctrlKey || event.shiftKey || event.button != 0))
      return;
    event.preventDefault();
    item.onClick?.({ commonDialogs });
  };
